/* eslint-disable no-param-reassign */
import { CanvasRendererFactory, CanvasState } from '../canvasrender';
import { CanvasOpIOS, ShaderType, FlagsIOS } from '@fullstory/mobile-flatbuffers';
import { Canvas, CanvasIOS, Rect, BitmapType } from '../view';
import {
  IOSRenderingModeEnum,
  IOSOrientationEnum,
  IOSContentModeEnum,
  IOSLineCapEnum,
  IOSLineJoinEnum,
  IOSBlurEnum,
  IOSVibrancyEnum,
  ASSET_FONTS,
} from '../resources';
import { Matrix } from '../matrix';
import { OffscreenCanvasCache } from '../offscreencanvascache';
import { logErrorOnce, logWarnOnce } from '../log_once';
import { serializeSelector } from '@fullstory/selectors';

const BLANK_IMG = new Image();
BLANK_IMG.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

export class IOSCanvasRendererFactory extends CanvasRendererFactory<CanvasOpIOS> {
  renderers: { [type: number]: Renderer } = {};
  drawsOp: { [type: number]: (op: CanvasIOS) => boolean } = {};

  constructor() {
    super();
    // Virtual op inserted by dataunpack
    this.renderers[CanvasOpIOS.RESET_ALL] = new ResetRenderer();

    this.renderers[CanvasOpIOS.COLOR_SET_STROKING] = new ColorSetRenderer();
    this.renderers[CanvasOpIOS.COLOR_SET_NONSTROKING] = new ColorSetRenderer();
    this.renderers[CanvasOpIOS.GS_SAVE] = new SaveRenderer();
    this.renderers[CanvasOpIOS.GS_RESTORE] = new RestoreRenderer();
    this.renderers[CanvasOpIOS.TEXT_BEGIN_OBJECT] = new SaveRenderer();
    this.renderers[CanvasOpIOS.TEXT_END_OBJECT] = new RestoreRenderer();
    this.renderers[CanvasOpIOS.GS_CONCAT_MATRIX] = new ConcatMatrixRenderer();
    this.renderers[CanvasOpIOS.PATH_RECT] = new PathRectRenderer();
    this.renderers[CanvasOpIOS.TEXT_MATRIX] = new TextMatrixRenderer();
    this.renderers[CanvasOpIOS.TEXT_NEXT_LINE] = new TextNextLineRenderer();
    this.renderers[CanvasOpIOS.PATH_CURVE_TO] = new PathCurveToRenderer();
    this.renderers[CanvasOpIOS.PATH_MOVE_TO] = new PathMoveToRenderer();
    this.renderers[CanvasOpIOS.PATH_LINE_TO] = new PathLineToRenderer();
    this.renderers[CanvasOpIOS.PATH_CLOSE] = new PathOpRenderer();
    this.renderers[CanvasOpIOS.PATH_END] = new PathOpRenderer();
    this.renderers[CanvasOpIOS.GS_DICT_ALPHA_NONSTROKING] = new SetAlphaRenderer();
    this.renderers[CanvasOpIOS.GS_DICT_ALPHA_STROKING] = new SetAlphaRenderer();
    this.renderers[CanvasOpIOS.GS_LINE_WIDTH] = new SetLineWidthRenderer();
    this.renderers[CanvasOpIOS.DRAW_CHILD] = new DrawChildRenderer();
    this.renderers[CanvasOpIOS.GS_LINE_CAP] = new LineCapRenderer();
    this.renderers[CanvasOpIOS.GS_LINE_JOIN] = new LineJoinRenderer();
    this.renderers[CanvasOpIOS.GS_MITER_LIMIT] = new MiterLimitRenderer();
    this.renderers[CanvasOpIOS.PATH_CLIP_EVEN_ODD] = new PathOpRenderer();
    this.renderers[CanvasOpIOS.PATH_CLIP_NONZERO] = new PathOpRenderer();
    // borders obviously *do* draw, but we don't want a view that only draws a border
    // to cover things up in inspect mode
    this.renderers[CanvasOpIOS.DRAW_BORDER] = new DrawBorderRenderer();

    this.addDrawingRenderer(CanvasOpIOS.PATH_CLOSE_STROKE, new PathOpRenderer());
    this.addDrawingRenderer(CanvasOpIOS.PATH_STROKE, new PathOpRenderer());
    this.addDrawingRenderer(CanvasOpIOS.PATH_FILL_NONZERO, new PathOpRenderer());
    this.addDrawingRenderer(CanvasOpIOS.PATH_FILL_EVEN_ODD, new PathOpRenderer());
    this.addDrawingRenderer(CanvasOpIOS.PATH_FILL_STROKE_EVEN_ODD, new PathOpRenderer());
    this.addDrawingRenderer(CanvasOpIOS.PATH_FILL_STROKE_NONZERO, new PathOpRenderer());
    this.addDrawingRenderer(CanvasOpIOS.PATH_CLOSE_FILL_STROKE_EVEN_ODD, new PathOpRenderer());
    this.addDrawingRenderer(CanvasOpIOS.PATH_CLOSE_FILL_STROKE_NONZERO, new PathOpRenderer());
    this.addDrawingRenderer(CanvasOpIOS.TEXT_SHOW, new TextShowRenderer());
    this.addDrawingRenderer(CanvasOpIOS.DRAW_IMAGE, new DrawImageRenderer());
    this.addDrawingRenderer(CanvasOpIOS.DRAW_IMAGE_VIEW, new DrawImageRenderer());
    this.addDrawingRenderer(CanvasOpIOS.DRAW_KEYBOARD, new DrawKeyboardRenderer());
    this.addDrawingRenderer(CanvasOpIOS.PAINT_SHADING, new PaintShaderRenderer());
    this.renderers[CanvasOpIOS.VISUAL_EFFECT_BLUR] = new VisualEffectBlurRenderer();
    this.drawsOp[CanvasOpIOS.VISUAL_EFFECT_BLUR] = VisualEffectBlurRenderer.prototype.draws.bind(
      this.renderers[CanvasOpIOS.VISUAL_EFFECT_BLUR],
    ); /* This one is not so simple. */
    this.addDrawingRenderer(CanvasOpIOS.VISUAL_EFFECT_VIBRANCY, new VisualEffectVibrancyRenderer());
  }

  addDrawingRenderer(op: CanvasOpIOS, renderer) {
    this.renderers[op] = renderer;
    this.drawsOp[op] = _ => true;
  }

  visibleForInspectMode(op: CanvasIOS) {
    return this.drawsOp[op.op] && this.drawsOp[op.op](op);
  }

  drawsChild(op: Canvas<CanvasOpIOS>) {
    const opIos = <CanvasIOS>op;
    switch (op.op) {
      case CanvasOpIOS.DRAW_CHILD:
        return opIos.viewId;
      default:
        return null;
    }
  }

  render(state: CanvasState, entry: CanvasIOS) {
    const renderer = this.renderers[entry.op];
    if (!renderer) {
      logWarnOnce(
        `No renderer for ${this.reverseLookup(CanvasOpIOS, entry.op)} (${entry.op}) on view ${
          state.view.uniqueId
        }`,
        entry,
        state,
      );
      return;
    }

    renderer.render(state, entry, state.saveStack[0].c2d);
  }
}

// TODO(mmastrac): This is duplicated code from the Android renderer, needs to be moved to a shared base class
abstract class Renderer {
  abstract render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D);

  protected drawMissingImage(c2d: CanvasRenderingContext2D, dst: Rect) {
    c2d.save();
    c2d.lineWidth = 1;
    c2d.fillStyle = '#eeeeee';
    c2d.fillRect(dst.x1, dst.y1, dst.width(), dst.height());
    c2d.strokeStyle = '#aaaaaa';
    c2d.beginPath();
    c2d.rect(dst.x1, dst.y1, dst.width(), dst.height());
    c2d.stroke();
    c2d.setLineDash([3, 3]);
    c2d.moveTo(dst.x1, dst.y1);
    c2d.lineTo(dst.x2, dst.y2);
    c2d.moveTo(dst.x1, dst.y2);
    c2d.lineTo(dst.x2, dst.y1);
    c2d.stroke();
    c2d.restore();
  }

  protected drawLoadingImage(c2d: CanvasRenderingContext2D, dst: Rect) {
    c2d.save();
    c2d.lineWidth = 1;
    c2d.fillStyle = '#eeeeee';
    c2d.fillRect(dst.x1, dst.y1, dst.width(), dst.height());
    c2d.strokeStyle = '#aaaaaa';
    c2d.beginPath();
    c2d.rect(dst.x1, dst.y1, dst.width(), dst.height());
    c2d.stroke();

    // draw three dots
    c2d.fillStyle = '#aaaaaa';
    const centerX = (dst.x1 + dst.x2) / 2;
    const centerY = (dst.y1 + dst.y2) / 2;
    // values are arbitrary to make sure it fits
    // we use up 8 radii of width and 2 of height,
    // this allows at least two full diameters padding in each dimension
    const radius = Math.min(dst.height() / 10, dst.width() / 16);
    c2d.beginPath();
    c2d.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    // one radius gap between circles
    c2d.arc(centerX + 3 * radius, centerY, radius, 0, 2 * Math.PI);
    c2d.arc(centerX - 3 * radius, centerY, radius, 0, 2 * Math.PI);
    c2d.fill();
    c2d.restore();
  }

  protected drawPrivateImage(c2d: CanvasRenderingContext2D, dominantColor: string, dst: Rect) {
    c2d.save();
    c2d.fillStyle = dominantColor;
    c2d.fillRect(dst.x1, dst.y1, dst.width(), dst.height());
    c2d.restore();
  }
}

class ResetRenderer extends Renderer {
  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    while (state.saveStack.length > 1) {
      state.pop();
    }
  }
}

class DrawBorderRenderer extends Renderer {
  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    c2d.save();
    c2d.strokeStyle = op.color;
    c2d.lineWidth = op.lineWidth;

    const hw = op.lineWidth / 2;
    c2d.roundRect(
      hw,
      hw,
      state.view.position.width() - 2 * hw,
      state.view.position.height() - 2 * hw,
      Math.max(op.value - hw, 0), // iOS allows negative corner radius, but browsers will throw a RangeError, breaking playback
    );
    c2d.stroke();
    c2d.restore();
  }
}

class PaintShaderRenderer extends Renderer {
  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    c2d.save();
    const shader = op.shader;
    if (shader) {
      const type = shader.type || ShaderType.LINEAR_GRADIENT;
      if (type == ShaderType.LINEAR_GRADIENT) {
        const gradient = c2d.createLinearGradient(
          shader.p1.x * state.width,
          shader.p1.y * state.height,
          shader.p2.x * state.width,
          shader.p2.y * state.height,
        );
        const positions = shader.positions.length ? shader.positions : [0, 1];
        for (let i = 0; i < positions.length; i++) {
          // TODO: figure out what more positions than colors means
          // for now, just don't crash
          const colorIndex = Math.min(shader.colors.length - 1, i);
          gradient.addColorStop(positions[i], shader.colors[colorIndex]);
        }
        c2d.fillStyle = gradient;
        c2d.fillRect(0, 0, state.width, state.height);
      }
    } else {
      // TODO: More general handling of this
      logWarnOnce(`Missing shader for PAINT_SHADING op on view ${state.view.uniqueId}`, op);
      c2d.fillStyle = '#eee';
      c2d.fillRect(0, 0, state.width, state.height);
    }
    c2d.restore();
  }
}

class DrawChildRenderer extends Renderer {
  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    state.drawChild(op.viewId, state.saveStack[0].matrix);
  }
}
class LineCapRenderer extends Renderer {
  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    switch (op.flag1) {
      case IOSLineCapEnum.BUTT:
        c2d.lineCap = 'butt';
        break;
      case IOSLineCapEnum.ROUND:
        c2d.lineCap = 'round';
        break;
      case IOSLineCapEnum.SQUARE:
        c2d.lineCap = 'square';
        break;
      default:
        logWarnOnce(`Unrecognized lineCap value: ${op.flag1} on view ${state.view.uniqueId}`, op);
    }
  }
}

class LineJoinRenderer extends Renderer {
  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    switch (op.flag1) {
      case IOSLineJoinEnum.BEVEL:
        c2d.lineJoin = 'bevel';
        break;
      case IOSLineJoinEnum.ROUND:
        c2d.lineJoin = 'round';
        break;
      case IOSLineJoinEnum.MITER:
        c2d.lineJoin = 'miter';
        break;
      default:
        logWarnOnce(`Unrecognized lineJoin value: ${op.flag1} on view ${state.view.uniqueId}`, op);
    }
  }
}
class MiterLimitRenderer extends Renderer {
  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    c2d.miterLimit = op.value;
  }
}

/* Replicated in FSWindowScannerPDFEncoder.mm/encodeDrawImageToEncoder. */
const FLAG1_CONTENTMODE = 0xf;
const FLAG1_FORCE_TINT = 0x10;

class DrawImageRenderer extends Renderer {
  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    c2d.save();
    let img: any /* grumble. */ = op.bitmap && op.bitmap.img;
    const isMasked = op.bitmap && op.bitmap.type == BitmapType.Masked;
    const validImage = img && img.width > 0 && img.height > 0;

    // TODO(mmastrac): This is a hack to work around playback exceptions with masked images going through this
    // drawing pipeline.
    if (!validImage && isMasked) {
      img = BLANK_IMG;
    }
    if (!validImage && !isMasked) {
      // we can't draw an image, by default show a placeholder "X"
      let drawMissingPlaceholder = true;
      // maybe log to explain why this image is missing
      if (op.bitmap) {
        const selector = serializeSelector(state.selectorChain());
        const viewString = `for view ${state.view.uniqueId} (${state.width} by ${state.height}) ${selector}`;
        if (op.bitmap.url) {
          // it's possible the image simply hasn't loaded yet, so don't assume there's a problem yet
          if (op.bitmap.loadFailed) {
            logWarnOnce(
              `missing image: failed to load from FullStory storage at ${op.bitmap.url} ${viewString}`,
            );
          } else {
            // don't log a warning, and draw a loading image instead of the missing image "X"
            drawMissingPlaceholder = false;
            this.drawLoadingImage(c2d, new Rect(0, 0, state.width, state.height));
          }
        } else if (op.bitmap.asset) {
          logWarnOnce(`missing image: couldn't resolve asset ${op.bitmap.asset} ${viewString}`);
        } else {
          // shouldn't happen
          logErrorOnce(`missing image: image source not recorded ${viewString}`);
        }
      }
      if (drawMissingPlaceholder)
        this.drawMissingImage(c2d, new Rect(0, 0, state.width, state.height));
    } else {
      let tintctx;
      let rotctx;

      /* If we need to, create a new image to tint. */
      const shouldtint =
        op.flag1 & FLAG1_FORCE_TINT ||
        (op.bitmap.params &&
          (op.bitmap.params.renderingMode == IOSRenderingModeEnum.AlwaysTemplate ||
            (op.bitmap.params.renderingMode == IOSRenderingModeEnum.Automatic &&
              op.bitmap.params.intrinsic &&
              op.bitmap.params.intrinsic.templateRenderingMode &&
              op.bitmap.params.intrinsic.templateRenderingMode ==
                'kCoreThemeTemplateRenderingModeTemplate')));
      if (shouldtint) {
        tintctx = OffscreenCanvasCache.getC2d(img.width, img.height);
        tintctx.globalCompositeOperation = 'copy';
        tintctx.drawImage(img, 0, 0, img.width, img.height);

        tintctx.globalCompositeOperation = 'source-in';
        const vibrancyTint = state.fromSaveStackBag('vibrancy');
        if (vibrancyTint) tintctx.fillStyle = vibrancyTint;
        else tintctx.fillStyle = state.view.tintColor;
        tintctx.fillRect(0, 0, img.width, img.height);

        img = tintctx.canvas;
      }

      /* If we need to, also, transform the image.
       *
       * In theory, we could merge this with the tint operation above if we
       * were worried about performance, but for right now, we'll keep it
       * simple until the profiler says so!
       *
       * XXX: Rotation (other than mirroring) is untested.
       */
      if (op.bitmap.params && op.bitmap.params.orientation != IOSOrientationEnum.Up) {
        const rot = op.bitmap.params.orientation;
        const isrot =
          rot == IOSOrientationEnum.Left ||
          rot == IOSOrientationEnum.Right ||
          rot == IOSOrientationEnum.LeftMirrored ||
          rot == IOSOrientationEnum.RightMirrored;
        const ismirror =
          rot == IOSOrientationEnum.UpMirrored ||
          rot == IOSOrientationEnum.DownMirrored ||
          rot == IOSOrientationEnum.LeftMirrored ||
          rot == IOSOrientationEnum.RightMirrored;
        const normrot =
          rot == IOSOrientationEnum.LeftMirrored
            ? IOSOrientationEnum.Left
            : rot == IOSOrientationEnum.RightMirrored
            ? IOSOrientationEnum.Right
            : rot == IOSOrientationEnum.UpMirrored
            ? IOSOrientationEnum.Up
            : rot == IOSOrientationEnum.DownMirrored
            ? IOSOrientationEnum.Down
            : rot;
        const rotimgwidth = isrot ? img.height : img.width;
        const rotimgheight = isrot ? img.width : img.height;

        rotctx = OffscreenCanvasCache.getC2d(rotimgwidth, rotimgheight);
        rotctx.save();
        rotctx.translate(rotimgwidth / 2, rotimgheight / 2);
        rotctx.rotate(
          Math.PI *
            (normrot == IOSOrientationEnum.Down
              ? 1.0
              : normrot == IOSOrientationEnum.Left
              ? 0.5
              : normrot == IOSOrientationEnum.Right
              ? -0.5
              : 0),
        );
        if (ismirror) rotctx.scale(-1, 1);
        rotctx.translate(-rotimgwidth / 2, -rotimgheight / 2);

        rotctx.drawImage(img, 0, 0);

        rotctx.restore();

        img = rotctx.canvas;
      }

      if (op.op == CanvasOpIOS.DRAW_IMAGE) {
        /* DRAW_IMAGE comes from a raw "Do" command in the PDF.  According
         * to Figure 4.27 of the PDF spec, images are mapped to the
         * (flipped!) unit square, and are scaled by the preceeding
         * GS_CONCAT_MATRIX to the size they need to be -- so we just paint
         * them in a (flipped!) unit square here, too.
         */
        c2d.save();
        c2d.scale(1, -1);
        if (img && img.height && img.width) {
          c2d.drawImage(img, 0, -1, 1, 1);
        }
        c2d.restore();
      } else {
        /* contentMode has a bunch of options for how to stretch -- or not
         * stretch -- an image.  We set up a bunch of flags in advance, and
         * then sort out the mechanics of actually blitting it later.
         *
         * 9patch'ed images, of course, are just 'special exciting'
         * stretched images...
         */

        let scale = 1.0;
        if (op.bitmap.params) {
          scale = op.bitmap.params.scale;
        }
        if (op.scaleOverride) {
          /* from encodeDrawImageToEncoder (layer.contents) path */
          scale = op.scaleOverride;
        }
        if (scale != 1.0 && scale != 2.0 && scale != 3.0) {
          logWarnOnce(
            `WARNING: scale is ${scale} (expected 1.0, 2.0, or 3.0) on view ${state.view.uniqueId}`,
            op,
          );
        }

        const pos = op.flag1 & FLAG1_CONTENTMODE; /* [UIImageView contentMode] */
        let stretch = false;
        let xofs = 0;
        const xleft = 0;
        const xright = state.width - op.bitmap.width / scale;
        const xctr = xright / 2;
        let yofs = 0;
        const ytop = 0;
        const ybot = state.height - op.bitmap.height / scale;
        const yctr = ybot / 2;
        let xsz = state.width; /* output size, for scale modes */
        let ysz = state.height;
        switch (pos) {
          case IOSContentModeEnum.ScaleToFill:
            stretch = true;
            break;
          case IOSContentModeEnum.ScaleAspectFill:
          case IOSContentModeEnum.ScaleAspectFit: {
            /* XXX: may need to clip for fill mode */
            stretch = true;
            const fit = pos == IOSContentModeEnum.ScaleAspectFit;

            if (fit !== state.width / state.height > op.bitmap.width / op.bitmap.height) {
              /* i.e., the output window is wider than the input image is
               * (or, the input image is taller than the output window is);
               * so we stretch to fill the width, and crop the top and
               * bottom.  (of course, we invert these if we're in "fit" mode
               * instead of "fill" mode!)
               */
              ysz = (state.width * op.bitmap.height) / op.bitmap.width;
              yofs = (state.height - ysz) / 2;
            } else {
              /* output is taller than the input (input wider than the
               * output); stretch to fill the height, crop the left and
               * right
               */
              xsz = (state.height * op.bitmap.width) / op.bitmap.height;
              xofs = (state.width - xsz) / 2;
            }

            break;
          }
          case IOSContentModeEnum.Center:
            xofs = xctr;
            yofs = yctr;
            break;
          case IOSContentModeEnum.Top:
            xofs = xctr;
            yofs = ytop;
            break;
          case IOSContentModeEnum.Bottom:
            xofs = xctr;
            yofs = ybot;
            break;
          case IOSContentModeEnum.Left:
            xofs = xleft;
            yofs = yctr;
            break;
          case IOSContentModeEnum.Right:
            xofs = xright;
            yofs = yctr;
            break;
          case IOSContentModeEnum.TopLeft:
            xofs = xleft;
            yofs = ytop;
            break;
          case IOSContentModeEnum.TopRight:
            xofs = xright;
            yofs = ytop;
            break;
          case IOSContentModeEnum.BottomLeft:
            xofs = xleft;
            yofs = ybot;
            break;
          case IOSContentModeEnum.BottomRight:
            xofs = xright;
            yofs = ybot;
            break;

          default:
          case IOSContentModeEnum.Redraw:
            stretch = true;
            logWarnOnce(
              `WARNING: unsupported [UIImageView contentMode] ${pos} on view ${state.view.uniqueId}`,
              op,
            );
            break;
        }

        /* XXX: plumb params ninePatchOverride through */
        if (isMasked) {
          this.drawPrivateImage(c2d, op.bitmap.dominantColor, new Rect(xofs, yofs, xsz, ysz));
        } else if (!stretch) {
          c2d.drawImage(img, xofs, yofs, op.bitmap.width / scale, op.bitmap.height / scale);
        } else if (op.ninePatch) {
          let n = op.ninePatch;
          const w = op.bitmap.width / scale; /* internal size */
          const h = op.bitmap.height / scale;
          const ow = xsz; /* output size */
          const oh = ysz;

          /* We get passed the 9patch parameters in bitmap-pixel coordinates
           * (though ninePatchOverride is in screen-pixel coordinates!).  So
           * we scale for now, but we'll need to be careful when we deal
           * with ninePatchOverride later.  XXX
           *
           * Note, of course, that we can't modify 'n' in place, since we
           * could render this op in multiple frames!
           */
          n = new Rect(n.x1 / scale, n.y1 / scale, n.x2 / scale, n.y2 / scale);

          let cx = xofs,
            cy = yofs;

          const drawChunk = (x1, y1, x2, y2, sh /* stretch H? */, sv /* stretch V? */) => {
            let nw = x2 - x1; /* region size in input image */
            let nh = y2 - y1;
            const cw = sh ? ow - (w - n.width()) : nw; /* screen-pixel width */
            const ch = sv ? oh - (h - n.height()) : nh;

            // Zero sized chunk?
            if (!nw) {
              if (sh && cw) {
                x2 = x1 + 1;
                nw = 1;
              } else {
                return ch;
              }
            }

            if (!nh) {
              if (sv && ch) {
                y2 = y1 + 1;
                nh = 1;
              } else {
                return ch;
              }
            }

            /* Unfortunately, we do not get pixel-exact scaling *after*
             * applying the crop specified by the source coordinates -- at
             * least, in Chrome -- but instead, we get scaling *before*
             * applying the crop.  This means that values from outside the
             * crop region can "leak" into the output, presenting as ugly
             * smears on highly stretched single-pixel borders!  So instead,
             * we create a temporary image if any scaling is to be done,
             * blit to that, and then blit from there to the main image with
             * the scaling.
             *
             * No, I am not joking.
             */
            if (nw * scale != cw || nh * scale != ch) {
              const sw = Math.round(nw * scale);
              const sh = Math.round(nh * scale);
              if (sw >= 1 && sh >= 1) {
                const scalectx = OffscreenCanvasCache.getC2d(sw, sh);
                scalectx.drawImage(
                  img,
                  x1 * scale,
                  y1 * scale,
                  nw * scale,
                  nh * scale,
                  0,
                  0,
                  nw * scale,
                  nh * scale,
                );
                c2d.drawImage(scalectx.canvas, cx, cy, cw, ch);
                OffscreenCanvasCache.putC2d(scalectx);
              }
            } else {
              /* No stretching?  We can take the 'easy option'. */
              c2d.drawImage(img, x1 * scale, y1 * scale, nw * scale, nh * scale, cx, cy, cw, ch);
            }
            cx += cw;
            return ch;
          };

          let ch;
          ch = drawChunk(0, 0, n.x1, n.y1, 0, 0); // TL
          ch = drawChunk(n.x1, 0, n.x2, n.y1, 1, 0); // TM (stretch h)
          ch = drawChunk(n.x2, 0, w, n.y1, 0, 0); // TR

          cx = xofs;
          cy += ch;

          ch = drawChunk(0, n.y1, n.x1, n.y2, 0, 1); // ML (stretch v)
          ch = drawChunk(n.x1, n.y1, n.x2, n.y2, 1, 1); // MM (stretch both)
          ch = drawChunk(n.x2, n.y1, w, n.y2, 0, 1); // MR (stretch v)

          cx = xofs;
          cy += ch;

          ch = drawChunk(0, n.y2, n.x1, h, 0, 0); // BL
          ch = drawChunk(n.x1, n.y2, n.x2, h, 1, 0); // BM (stretch h)
          ch = drawChunk(n.x2, n.y2, w, h, 0, 0); // BR
        } else {
          c2d.drawImage(img, xofs, yofs, xsz, ysz);
        }
      }

      if (tintctx) {
        OffscreenCanvasCache.putC2d(tintctx);
        tintctx = null;
      }
      if (rotctx) {
        OffscreenCanvasCache.putC2d(rotctx);
        rotctx = null;
      }
    }
    c2d.restore();
  }
}

export class DrawKeyboardRenderer extends Renderer {
  // expected dimensions of the placeholder image
  readonly PLACEHOLDER_WIDTH = 1125;
  readonly PLACEHOLDER_HEIGHT = 1008;
  // height of specific portions of the placeholder image - should add up to PLACEHOLDER_HEIGHT
  readonly PLACEHOLDER_PREDICTIONS_HEIGHT = 140; // suggestions area
  readonly PLACEHOLDER_KEYBOARD_HEIGHT = 650; // keyboard itself
  readonly PLACEHOLDER_BOTTOM_HEIGHT = 218; // area below the kb with emoji and microphone buttons (only on iPhones with no home button)

  keyboard: HTMLImageElement;

  constructor() {
    super();
    this.keyboard = new Image();
    this.keyboard.src = `${window.location.protocol}//${window.location.host}/s/img/svg/ios-keyboard-placeholder.svg`;
    this.keyboard.onload = () => {
      // since we do math based on hard-coded pixel locations within this image, validate that the image is the expected size
      if (
        this.keyboard.width !== this.PLACEHOLDER_WIDTH ||
        this.keyboard.height !== this.PLACEHOLDER_HEIGHT
      ) {
        console.error(
          `iOS keyboard placeholder graphic is ${this.keyboard.width}x${this.keyboard.height}, expected 1125x1008 - rendering may be incorrect`,
        );
      }
    };
  }

  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    // The keyboard placeholder graphic includes suggestions above the keyboard and emoji & microphone buttons below ("thumb landings").
    // Because we don't record any details of the keyboard, we don't directly know if these should be displayed.
    // But we can infer it based on the width/height ratio of the keyboard.
    // These breakpoints were determined by testing about a dozen devices, and should provide a decent heuristic.
    // (We could also determine most of this from the device's model and OS version if we wanted to do the work to map *everything*.)
    const ratio = state.width / state.height;
    let showPredictiveText = false; // suggestions
    let showBottomButtons = false; // emoji and microphone buttons
    if (ratio > 2) {
      // either an iPad or something in landscape mode
      // the placeholder graphic isn't quite correct for an iOS device in landscape mode, the bottom area actually moves to the sides
      // todo: make a wider placeholder image that also has emoji/mic buttons on the sides, then crop to just the center when appropriate
      showPredictiveText = true;
      showBottomButtons = false;
    } else if (ratio > 1.7) {
      // probably an older iPhone (with a home button) in a password field
      showPredictiveText = false;
      showBottomButtons = false;
    } else if (ratio > 1.44) {
      // probably an iPhone with a home button in a clear text field
      showPredictiveText = true;
      showBottomButtons = false;
    } else if (ratio > 1.25) {
      // probably a newer iPhone in a password field
      showPredictiveText = false;
      showBottomButtons = true; // technically we should just show empty space here for password fields
    } else {
      // probably a newer iPhone in a cleartext field
      showPredictiveText = true;
      showBottomButtons = true;
    }

    // source parameters - what parts of the placeholder graphic to use
    const sx = 0;
    const sy = showPredictiveText ? 0 : this.PLACEHOLDER_PREDICTIONS_HEIGHT; // skip over the top portion of the image if suggestions are hidden
    const sWidth = this.PLACEHOLDER_WIDTH;
    const sHeight =
      this.PLACEHOLDER_KEYBOARD_HEIGHT +
      (showPredictiveText ? this.PLACEHOLDER_PREDICTIONS_HEIGHT : 0) +
      (showBottomButtons ? this.PLACEHOLDER_BOTTOM_HEIGHT : 0); // combined height of all shown portions

    // destination parameters - where to draw it on the screen
    const dx = 0; // note: a matrix transform is in play that changes 0,0 to where the top left corner of the view is
    const dy = 0;
    const dWidth = state.width;
    const dHeight = state.height;

    c2d.drawImage(this.keyboard, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight);
  }
}

class ColorSetRenderer extends Renderer {
  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    if (op.op == CanvasOpIOS.COLOR_SET_STROKING) {
      c2d.strokeStyle = op.color;
    } else if (op.op == CanvasOpIOS.COLOR_SET_NONSTROKING) {
      c2d.fillStyle = op.color;
    } else {
      logWarnOnce(`Invalid op for ColorSetRenderer: ${op.op} on view ${state.view.uniqueId}`, op);
    }
  }
}

class SaveRenderer extends Renderer {
  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    const top = state.saveStack[0];
    state.saveStack.unshift({
      matrix: top.matrix.slice(),
      bag: {},
      c2d: top.c2d,
      fn: entry => {
        entry.c2d.restore();
      },
    });
    c2d.save();
  }
}

class RestoreRenderer extends Renderer {
  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    if (state.saveStack.length > 1) {
      state.pop();
    } else {
      // TODO(mmastrac): This needs to be fixed (we're currently adding extra GS_RESTORE calls)
      logWarnOnce(
        `Busted restore (no saved state to restore to) on view ${state.view.uniqueId}`,
        op,
      );
    }
  }
}

class ConcatMatrixRenderer extends Renderer {
  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    const m = Matrix.affinize(op.matrix);
    state.saveStack[0].matrix = Matrix.multiply(m, state.saveStack[0].matrix);
    Matrix.appendMatrix(m, c2d);
  }
}

class TextMatrixRenderer extends Renderer {
  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    /* Most things in PDF, including text, are rendered with a flipped
     * matrix.  So we flip the text matrix, too.  */
    const m = Matrix.identity();
    m[4] = -m[4];

    state.textMatrix = state.textLineMatrix = Matrix.multiply(m, Matrix.affinize(op.matrix));
  }
}

class TextNextLineRenderer extends Renderer {
  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    const p = op.point;
    const m = Matrix.translation(p.y, p.x);

    state.textMatrix = state.textLineMatrix = Matrix.multiply(m, state.textLineMatrix);
  }
}

class SetAlphaRenderer extends Renderer {
  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    state.saveStack[0].bag[
      op.op == CanvasOpIOS.GS_DICT_ALPHA_NONSTROKING ? 'alpha_ns' : 'alpha_s'
    ] = op.value;
  }
}

class SetLineWidthRenderer extends Renderer {
  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    c2d.lineWidth = op.lineWidth;
  }
}

class TextShowRenderer extends Renderer {
  static SanFrancisco =
    '-apple-system, BlinkMacSystemFont, SFUIText, HelveticaNeue-Light, Helvetica, sans-serif';
  static NewYork = '-apple-system-ui-serif, ui-serif, Georgia, serif';
  static fontTable = {
    /* original, font-family, font-style / variant / weight */
    'Phone Fallback': { family: TextShowRenderer.SanFrancisco },
    SFUI: { family: TextShowRenderer.SanFrancisco },
    'SFUI Text': { family: TextShowRenderer.SanFrancisco },
    'SFUI Display': { family: TextShowRenderer.SanFrancisco },
    'New York': { family: TextShowRenderer.NewYork },
    Courier: { family: "Courier, 'Courier New', monospace" },
    'Geeza Pro': { family: "'Geeza Pro'" },
  };
  // https://iosfontlist.com/fonts.json
  // grep -o '"font":"[^"]*","preinstalled":"[^0]' fonts.json | grep -o '"[A-Z][^"]*",'
  // Some fonts are not installed by default on macOS - see Font Book app for details
  static readonly installedFontList: Set<String> = new Set([
    'AcademyEngravedLetPlain',
    'AlNile',
    'AlNile-Bold',
    'AmericanTypewriter-Light',
    'AmericanTypewriter',
    'AmericanTypewriter-SemiBold',
    'AmericanTypewriter-Bold',
    'AmericanTypewriter-CondensedLight',
    'AmericanTypewriter-Condensed',
    'AmericanTypewriter-CondensedBold',
    'AppleColorEmoji',
    'AppleSDGothicNeo-UltraLight',
    'AppleSDGothicNeo-Thin',
    'AppleSDGothicNeo-Light',
    'AppleSDGothicNeo-Regular',
    'AppleSDGothicNeo-Medium',
    'AppleSDGothicNeo-SemiBold',
    'AppleSDGothicNeo-Bold',
    'ArialMT',
    'Arial-ItalicMT',
    'Arial-BoldMT',
    'Arial-BoldItalicMT',
    'ArialHebrew-Light',
    'ArialHebrew',
    'ArialHebrew-Bold',
    'ArialRoundedMTBold',
    'Avenir-Light',
    'Avenir-LightOblique',
    'Avenir-Book',
    'Avenir-BookOblique',
    'Avenir-Roman',
    'Avenir-Oblique',
    'Avenir-Medium',
    'Avenir-MediumOblique',
    'Avenir-Heavy',
    'Avenir-HeavyOblique',
    'Avenir-Black',
    'Avenir-BlackOblique',
    'AvenirNext-UltraLight',
    'AvenirNext-UltraLightItalic',
    'AvenirNext-Regular',
    'AvenirNext-Italic',
    'AvenirNext-Medium',
    'AvenirNext-MediumItalic',
    'AvenirNext-DemiBold',
    'AvenirNext-DemiBoldItalic',
    'AvenirNext-Bold',
    'AvenirNext-BoldItalic',
    'AvenirNext-Heavy',
    'AvenirNext-HeavyItalic',
    'AvenirNextCondensed-UltraLight',
    'AvenirNextCondensed-UltraLightItalic',
    'AvenirNextCondensed-Regular',
    'AvenirNextCondensed-Italic',
    'AvenirNextCondensed-Medium',
    'AvenirNextCondensed-MediumItalic',
    'AvenirNextCondensed-DemiBold',
    'AvenirNextCondensed-DemiBoldItalic',
    'AvenirNextCondensed-Bold',
    'AvenirNextCondensed-BoldItalic',
    'AvenirNextCondensed-Heavy',
    'AvenirNextCondensed-HeavyItalic',
    'BanglaSangamMN',
    'BanglaSangamMN-Bold',
    'Baskerville',
    'Baskerville-Italic',
    'Baskerville-SemiBold',
    'Baskerville-SemiBoldItalic',
    'Baskerville-Bold',
    'Baskerville-BoldItalic',
    'BodoniSvtyTwoITCTT-Book',
    'BodoniSvtyTwoITCTT-BookIta',
    'BodoniSvtyTwoITCTT-Bold',
    'BodoniSvtyTwoOSITCTT-Book',
    'BodoniSvtyTwoOSITCTT-BookIt',
    'BodoniSvtyTwoOSITCTT-Bold',
    'BodoniSvtyTwoSCITCTT-Book',
    'BodoniOrnamentsITCTT',
    'BradleyHandITCTT-Bold',
    'ChalkboardSE-Light',
    'ChalkboardSE-Regular',
    'ChalkboardSE-Bold',
    'Chalkduster',
    'Cochin',
    'Cochin-Italic',
    'Cochin-Bold',
    'Cochin-BoldItalic',
    'Copperplate-Light',
    'Copperplate',
    'Copperplate-Bold',
    'CourierNewPSMT',
    'CourierNewPS-ItalicMT',
    'CourierNewPS-BoldMT',
    'CourierNewPS-BoldItalicMT',
    'DamascusLight',
    'Damascus',
    'DamascusMedium',
    'DamascusSemiBold',
    'DamascusBold',
    'DevanagariSangamMN',
    'DevanagariSangamMN-Bold',
    'Didot',
    'Didot-Italic',
    'Didot-Bold',
    'DINAlternate-Bold',
    'DINCondensed-Bold',
    'EuphemiaUCAS',
    'EuphemiaUCAS-Italic',
    'EuphemiaUCAS-Bold',
    'Farah',
    'Futura-Medium',
    'Futura-MediumItalic',
    'Futura-Bold',
    'Futura-CondensedMedium',
    'Futura-CondensedExtraBold',
    'Georgia',
    'Georgia-Italic',
    'Georgia-Bold',
    'Georgia-BoldItalic',
    'GillSans-Light',
    'GillSans-LightItalic',
    'GillSans',
    'GillSans-Italic',
    'GillSans-SemiBold',
    'GillSans-SemiBoldItalic',
    'GillSans-Bold',
    'GillSans-BoldItalic',
    'GillSans-UltraBold',
    'GujaratiSangamMN',
    'GujaratiSangamMN-Bold',
    'GurmukhiMN',
    'GurmukhiMN-Bold',
    'STHeitiSC-Light',
    'STHeitiSC-Medium',
    'STHeitiTC-Light',
    'STHeitiTC-Medium',
    'Helvetica-Light',
    'Helvetica-LightOblique',
    'Helvetica',
    'Helvetica-Oblique',
    'Helvetica-Bold',
    'Helvetica-BoldOblique',
    'HelveticaNeue-UltraLight',
    'HelveticaNeue-UltraLightItalic',
    'HelveticaNeue-Thin',
    'HelveticaNeue-ThinItalic',
    'HelveticaNeue-Light',
    'HelveticaNeue-LightItalic',
    'HelveticaNeue',
    'HelveticaNeue-Italic',
    'HelveticaNeue-Medium',
    'HelveticaNeue-MediumItalic',
    'HelveticaNeue-Bold',
    'HelveticaNeue-BoldItalic',
    'HelveticaNeue-CondensedBold',
    'HelveticaNeue-CondensedBlack',
    'HiraKakuProN-W3',
    'HiraKakuProN-W6',
    'HiraMinProN-W3',
    'HiraMinProN-W6',
    'HiraginoSans-W3',
    'HiraginoSans-W6',
    'HoeflerText-Regular',
    'HoeflerText-Italic',
    'HoeflerText-Black',
    'HoeflerText-BlackItalic',
    'Kailasa',
    'Kailasa-Bold',
    'KannadaSangamMN',
    'KannadaSangamMN-Bold',
    'KhmerSangamMN',
    'KohinoorBangla-Light',
    'KohinoorBangla-Regular',
    'KohinoorBangla-Semibold',
    'KohinoorDevanagari-Light',
    'KohinoorDevanagari-Regular',
    'KohinoorDevanagari-Book',
    'KohinoorDevanagari-Medium',
    'KohinoorDevanagari-Semibold',
    'KohinoorTelugu-Light',
    'KohinoorTelugu-Regular',
    'KohinoorTelugu-Medium',
    'LaoSangamMN',
    'MalayalamSangamMN',
    'MalayalamSangamMN-Bold',
    'MarkerFelt-Thin',
    'MarkerFelt-Wide',
    'Menlo-Regular',
    'Menlo-Italic',
    'Menlo-Bold',
    'Menlo-BoldItalic',
    'MyanmarSangamMN',
    'MyanmarSangamMN-Bold',
    'Noteworthy-Light',
    'Noteworthy-Bold',
    'Optima-Regular',
    'Optima-Italic',
    'Optima-Bold',
    'Optima-BoldItalic',
    'Optima-ExtraBlack',
    'OriyaSangamMN',
    'OriyaSangamMN-Bold',
    'Palatino-Roman',
    'Palatino-Italic',
    'Palatino-Bold',
    'Palatino-BoldItalic',
    'Papyrus',
    'Papyrus-Condensed',
    'PartyLetPlain',
    'PingFangHK-Ultralight',
    'PingFangHK-Thin',
    'PingFangHK-Light',
    'PingFangHK-Regular',
    'PingFangHK-Medium',
    'PingFangHK-Semibold',
    'PingFangSC-Ultralight',
    'PingFangSC-Thin',
    'PingFangSC-Light',
    'PingFangSC-Regular',
    'PingFangSC-Medium',
    'PingFangSC-Semibold',
    'PingFangTC-Ultralight',
    'PingFangTC-Thin',
    'PingFangTC-Light',
    'PingFangTC-Regular',
    'PingFangTC-Medium',
    'PingFangTC-Semibold',
    'SavoyeLetPlain',
    'SinhalaSangamMN',
    'SinhalaSangamMN-Bold',
    'SnellRoundhand',
    'SnellRoundhand-Bold',
    'SnellRoundhand-Black',
    'Symbol',
    'TamilSangamMN',
    'TamilSangamMN-Bold',
    'TeluguSangamMN',
    'TeluguSangamMN-Bold',
    'Thonburi-Light',
    'Thonburi',
    'Thonburi-Bold',
    'TimesNewRomanPSMT',
    'TimesNewRomanPS-ItalicMT',
    'TimesNewRomanPS-BoldMT',
    'TimesNewRomanPS-BoldItalicMT',
    'TrebuchetMS',
    'TrebuchetMS-Italic',
    'TrebuchetMS-Bold',
    'Trebuchet-BoldItalic',
    'Verdana',
    'Verdana-Italic',
    'Verdana-Bold',
    'Verdana-BoldItalic',
    'ZapfDingbatsITC',
    'Zapfino',
  ]);

  // separator is typically - for built-in fonts and _ for asset fonts
  parseModifiers(
    font: string,
    overrideweight: string | null,
    separator: string,
  ): [string, string, string, string] {
    const weightTable: { [weight: string]: number } = {
      Thin: 100,
      ExtraLight: 200,
      UltraLight: 200,
      Ultralight: 200,
      Light: 300,
      Book: 350,
      Regular: 400,
      Medium: 500,
      SemiBold: 600,
      Semibold: 600,
      DemiBold: 600,
      Bold: 700,
      ExtraBold: 800,
      UltraBold: 800,
      Black: 900,
      Heavy: 900,
    };

    // Normalize installed font name and incorporate modifiers embedded in name into font parameters
    let fontweight = '';
    let fontstyle = '';
    let fontstretch = '';

    // Parse font style
    if (font.match(new RegExp(`${separator}.*Italic$`))) {
      fontstyle = 'italic';
      // Leave the separator because Italic is next to other modifiers when they are present, e.g. -RegularItalic
      font = font.replace(/Italic$/, '');
    } else if (font.match(new RegExp(`${separator}.*Oblique$`))) {
      fontstyle = 'oblique';
      font = font.replace(/Oblique$/, '');
    }
    // Parse font stretch
    if (font.match(/Condensed/)) {
      // "Condensed" can appear before or after the separator in the font name
      fontstretch = 'condensed';
      font = font.replace(/Condensed/, '');
    }

    // Parse font weight
    // Names and weights from https://developer.mozilla.org/en-US/docs/Web/CSS/font-weight#common_weight_name_mapping
    // with some additional and variant names from iOS Font List
    // additional Font List weight values from https://bigelowandholmes.typepad.com/bigelow-holmes/2015/07/on-font-weight.html
    if (overrideweight) {
      fontweight = overrideweight.toString();
    } else if (font.endsWith(separator)) {
      fontweight = '';
      font = font.substring(0, font.length - 1);
    } else {
      for (const weight in weightTable) {
        if (font.endsWith(separator + weight)) {
          fontweight = weightTable[weight].toString();
          font = font.substring(0, font.length - separator.length - weight.length);
          break;
        }
      }
    }

    // Remove final separator and trailing modifiers/metrics, if any
    const lastIndex = font.lastIndexOf(separator);
    if (lastIndex > -1) {
      font = font.substring(0, lastIndex);
    }
    return [font, fontweight, fontstyle, fontstretch];
  }

  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    // TODO(mmastrac): Should probably not be sending these from the client
    if (op.text == null || op.text.length == 0) {
      logWarnOnce(
        `Null or empty text for TextShadowRenderer on view ${state.view.uniqueId}`,
        op,
        state,
      );
      return;
    }

    c2d.save();

    Matrix.appendMatrix(state.textMatrix, c2d);

    let font = op.textFont || '.SFUIText';
    if (font.substring(0, 1) == '.') font = font.substring(1);

    /* Try to parse out any of its friends on the end.
     * XXX: DRY
     *
     * One problem is that fonts that we package in fact have modifiers on
     * the end.  Rather than parsing those twice (i.e., embedding the same
     * logic into the CSS dynamic font generators that we have in
     * renderer/mobile/renderer.ts), we cheat: instead of using the new
     * parsed-down font as what we append to 'dynamic-', we use the original
     * font name that came from iOS, while still falling back on the system
     * font if needed.
     */
    const origfont = font;
    let fontweight = '';
    let overrideweight = null;
    let fontstyle = '';
    let fontstretch = '';

    if (!ASSET_FONTS.has(origfont)) {
      // this is an attempt to parse variable fonts like SF and NY
      // if font name contains "_wght" followed by an all caps hex string, it's a weight value
      // empty weight value corresponds to "regular" font weight
      let fontnameweight = font.match(/_wght([A-F0-9]*)(_|$)/);
      let fontopsz = font.match(/_opsz([A-F0-9]*)(_|$)/);
      // Strip trailing metrics: must have _opsz and _wght but could have other _metrics
      if (fontnameweight && fontopsz) {
        font = font.replace(/(-[^_]*)_.*$/, '$1');
      }
      if (fontnameweight) {
        // Assume font is dynamic - parse weight/style and strip remainder
        if (fontnameweight[1].length == 0) {
          fontweight = '400'; // regular
        } else {
          // "magic divisor" observed in practice: bold was exactly 700 with this divisor
          overrideweight = Math.round(parseInt(fontnameweight[1], 16) / 65536.0);
          // correct for weights above 400 looking wrong (somehow, 510 and 590 are bolder than 700?)
          if (overrideweight > 400) {
            overrideweight = Math.round(overrideweight / 100) * 100;
          }
        }
      }

      // Asset fonts may have modifiers as well, using _ instead of -
      [font, fontweight, fontstyle, fontstretch] = this.parseModifiers(
        font,
        overrideweight,
        font.indexOf('_') != -1 && ASSET_FONTS.has(font.substring(0, font.indexOf('_')))
          ? '_'
          : '-',
      );

      // Remove trailing Monotype foundary, not part of the real font name, e.g. ArialMT -> Arial. Does not cover e.g. ArialRoundedMTBold.
      font = font.replace(/([a-z])MT$/, '$1');

      // Convert a CamelCase name into a word sequence, e.g. NewYork -> New York, SFArabic -> SF Arabic, AppleSDGothicNeo -> Apple SD Gothic Neo, ChalkboardSE -> Chalkboard SE
      font = font.replace(/([a-z])([A-Z])/g, '$1 $2');
      font = font.replace(/([A-Z])([A-Z])([a-z])/g, '$1 $2$3');

      if (font == 'SF Arabic' && fontweight) {
        // XXX: SF Arabic "regular" weight appears to be too heavy
        let numweight = parseInt(fontweight);
        if (numweight <= 400 && numweight >= 225) {
          fontweight = '225';
        }
      }

      if (
        TextShowRenderer.fontTable[font] &&
        TextShowRenderer.fontTable[font].family === TextShowRenderer.SanFrancisco &&
        ((overrideweight && overrideweight > 400) || op.textCharSpacing < 0)
      ) {
        // XXX: force negative letter spacing for SF font corresponding to appearance on device
        // when textCharSpacing is negative or the font is heavy weight
        // this spacing value looked good for all text meeting these rules, regardless of opsz values,
        // (10000, 140000, 160000, 220000) though opsz should control negative spacing in practice
        // For an example of non-negative char spacing, look at "One of these days!" at 1:05
        // in https://app.staging.fullstory.com/ui/KWH/session/6397946398702500972:5928405853996976088
        // With the negative letter spacing, the "days" will separate from the "!"
        (c2d as any).letterSpacing = '-.09em';
      }
    }

    /* Look up the font in a table that we can try. */
    let fontfamily = null;
    if (ASSET_FONTS.has(origfont)) {
      /* There's no need to be nervous about fonts in assets. */
      fontfamily = `'dynamic-${origfont.replace(/'/g, '')}', ${TextShowRenderer.SanFrancisco}`;
    } else if (ASSET_FONTS.has(font)) {
      fontfamily = `'dynamic-${font.replace(/'/g, '')}', ${TextShowRenderer.SanFrancisco}`;
    } else if (TextShowRenderer.installedFontList.has(origfont)) {
      /* There's no need to be nervous about known installed fonts */
      fontfamily = `'${origfont.replace(/'/g, '')}', ${TextShowRenderer.SanFrancisco}`;
    } else if (font in TextShowRenderer.fontTable) {
      const ent = TextShowRenderer.fontTable[font];
      fontfamily = ent['family'];
      if (!fontweight && 'weight' in ent) {
        fontweight = ent['weight'];
      }
    } else {
      // iOS allows systemwide app-installed fonts and downloading fonts on demand:
      // https://developer.apple.com/documentation/xcode/configuring-custom-fonts
      // We include the original font name as a backup in case the reformatting heuristic doesn't work
      console.log('Using font ' + font + ' which may require installation to view');
      fontfamily = `'${font.replace(/'/g, '')}', '${origfont.replace(/'/g, '')}', ${
        TextShowRenderer.SanFrancisco
      }`;
      TextShowRenderer.fontTable[font] = { family: fontfamily };
    }

    // include spaces in font attributes if present (empty string is falsy)
    if (fontweight) {
      fontweight += ' ';
    }
    if (fontstyle) {
      fontstyle += ' ';
    }
    if (fontstretch) {
      fontstretch += ' ';
    }
    c2d.font = `${fontweight}${fontstyle}${fontstretch}${op.textSize}px ${fontfamily}`;
    c2d.textBaseline = 'alphabetic';

    /* Detect RTL text, and try to reverse it -- since PDF data comes in
     * glyph order left-to-right, rather than in linguistic order, but if we
     * render multiple characters in glyph order, then renderText will
     * actually render it reversed.
     */

    let text = op.text;

    const re_rtl = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/g;
    const re_ltr = /[^\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/g; /* "Approximately. */

    const n_rtl = (text.match(re_rtl) || []).length;
    const n_ltr = (text.match(re_ltr) || []).length;

    // if the text was actually drawn RTL, no need to reverse it
    const alreadyRTL = op.textBounds && op.textBounds.x < 0;
    if (!alreadyRTL && n_rtl > n_ltr) {
      if (n_ltr) {
        logWarnOnce(
          `WARNING: string "${text}" is suspicious, with rtl count ${n_rtl} and ltr count ${n_ltr}, but I'm reversing it anyway. On view ${state.view.uniqueId}`,
          op,
          state,
        );
      }
      text = text.split('').reverse().join('');
    }

    /* We horizontally squash the canvas rendered text to match the width
     * obtained from PDF capture to make sure everything fits.
     * Especially important if we are entirely missing a font and need to
     * wing it with a font that might have radically different metrics.
     */

    const metrics = c2d.measureText(text);
    let width = metrics.width;
    let specwidth = width;
    let totalXOffset = width;
    // the starting X coordinate for rendering the text or masking rectangles
    // usually zero, but negative for right-to-left rendering
    let startX = 0;
    if (op.textBounds) {
      // the original width measurement is in "glyph space", with size 1/1000
      // of the text space unit here. We divide by 100 on device (and quantize),
      // so include another factor of 10 here.
      totalXOffset = op.textBounds.x / 10;
      specwidth = Math.abs(totalXOffset);
      // if the offset is negative, we're moving left, and 0 is actually the *right* end
      // of the bounding box. Our rendering needs to start the text at the *left* end,
      // which is where we'll be after the offset.
      if (totalXOffset < 0) startX = totalXOffset;
    }
    if (!op.textPrivate && specwidth > 0.2 && width > specwidth) {
      /* Crush to fit if needed.  Note that we only do this on large enough
       * "width"s, so we don't need to stabilize the denominator here (we in
       * fact often get glyphs with a specified width of 0 -- like
       * diacritics!).  */

      c2d.scale(specwidth / width, 1.0);
      startX *= width / specwidth; // undo the scaling, we want the value in the original coordinate system
      width = specwidth;
    }

    /* What operations should we execute? */
    let stroke = false;
    let fill = false;
    switch (op.textRender) {
      case 0:
        fill = true;
        break;
      case 1:
        stroke = true;
        break;
      case 2:
        fill = stroke = true;
        break;
      case 3:
        break;
      default:
        logWarnOnce(
          `WARNING: unsupported PDF Tr clipping mode ${op.textRender} on view ${state.view.uniqueId}`,
        );
    }
    if (op.textPrivate) {
      const metrics = c2d.measureText(`${text}jpqfXY`); /* What an unpleasant hack. */
      const realSize = metrics.actualBoundingBoxAscent || op.textSize;
      const realWidth = specwidth;
      // We can't adjust alpha here because there are a few places on iOS where we're clearly not measuring bounding
      // boxes properly and they overlap.
      // let oldAlpha = c2d.globalAlpha;
      // c2d.globalAlpha *= 0.75;
      if (realWidth > realSize / 2) {
        c2d.beginPath();
        c2d.roundRect(startX, -realSize, realWidth, realSize, realSize * 0.25);
        c2d.fill();
      } else {
        // Small characters
        c2d.fillRect(startX - realWidth * 0.1, -realSize, realWidth * 1.2, realSize);
      }
      // c2d.globalAlpha = oldAlpha;
    }
    if (fill && !op.textPrivate) {
      const alpha_ns = state.fromSaveStackBag('alpha_ns');
      if (alpha_ns !== null) {
        c2d.globalAlpha = alpha_ns;
      }
      c2d.fillText(text, startX, 0);
    }
    if (stroke && !op.textPrivate) {
      /* Today in 'you gotta be freakin kidding me' news: "The graphics
       * state parameters affecting those operations, such as line width,
       * are interpreted in user space rather than in text space." So we
       * have to divide the line width down by the textMatrix scale factor.
       * Worse, we can't *really* know that, because, in theory, the
       * textMatrix could have asymmetric scale.  So we do the best we can.
       */
      c2d.lineWidth *=
        (c2d.lineWidth * 2.0) / (Math.abs(state.textMatrix[0]) + Math.abs(state.textMatrix[4]));
      const alpha_s = state.fromSaveStackBag('alpha_s');
      if (alpha_s !== null) {
        c2d.globalAlpha = alpha_s;
      }
      c2d.strokeText(text, startX, 0);
    }

    /* XXX: Something is not quite right here.  Arabic 'looks like Arabic',
     * except on a closer look, the ligatures are wrong, and the letters are
     * not in the right order!  Also, the only way we get the right output
     * here is if we flip dx and dy.  Something is very suspicious.
     */
    // TODO(mmastrac): I swapped x/y because it was clearly broken w/Nationwide's app. Need to revisit
    state.textMatrix = Matrix.multiply(Matrix.translation(totalXOffset, 0), state.textMatrix);

    c2d.restore();
  }
}

class PathMoveToRenderer extends Renderer {
  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    c2d.moveTo(op.point.x, op.point.y);
  }
}

class PathLineToRenderer extends Renderer {
  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    c2d.lineTo(op.point.x, op.point.y);
  }
}

class PathCurveToRenderer extends Renderer {
  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    c2d.bezierCurveTo(op.point3.x, op.point3.y, op.point2.x, op.point2.y, op.point.x, op.point.y);
  }
}

class PathRectRenderer extends Renderer {
  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    c2d.beginPath();
    if (op.value > 0) {
      c2d.roundRect(op.dest.x1, op.dest.y1, op.dest.width(), op.dest.height(), op.value);
    } else {
      c2d.rect(op.dest.x1, op.dest.y1, op.dest.width(), op.dest.height());
    }
  }
}

class PathOpRenderer extends Renderer {
  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    const end = op.op == CanvasOpIOS.PATH_END;
    const close =
      op.op == CanvasOpIOS.PATH_CLOSE_FILL_STROKE_EVEN_ODD ||
      op.op == CanvasOpIOS.PATH_CLOSE_FILL_STROKE_NONZERO ||
      op.op == CanvasOpIOS.PATH_CLOSE ||
      op.op == CanvasOpIOS.PATH_CLOSE_STROKE;

    const fill =
      [
        CanvasOpIOS.PATH_FILL_NONZERO,
        CanvasOpIOS.PATH_FILL_EVEN_ODD,
        CanvasOpIOS.PATH_FILL_STROKE_NONZERO,
        CanvasOpIOS.PATH_FILL_STROKE_EVEN_ODD,
        CanvasOpIOS.PATH_CLOSE_FILL_STROKE_NONZERO,
        CanvasOpIOS.PATH_CLOSE_FILL_STROKE_EVEN_ODD,
      ].indexOf(op.op) != -1;

    const fillRule =
      op.op == CanvasOpIOS.PATH_FILL_NONZERO ||
      op.op == CanvasOpIOS.PATH_FILL_STROKE_NONZERO ||
      op.op == CanvasOpIOS.PATH_CLOSE_FILL_STROKE_NONZERO ||
      CanvasOpIOS.PATH_CLIP_NONZERO
        ? 'nonzero'
        : 'evenodd';

    const stroke =
      [
        CanvasOpIOS.PATH_STROKE,
        CanvasOpIOS.PATH_CLOSE_STROKE,
        CanvasOpIOS.PATH_FILL_STROKE_NONZERO,
        CanvasOpIOS.PATH_FILL_STROKE_EVEN_ODD,
        CanvasOpIOS.PATH_CLOSE_FILL_STROKE_NONZERO,
        CanvasOpIOS.PATH_CLOSE_FILL_STROKE_EVEN_ODD,
      ].indexOf(op.op) != -1;

    const clip = op.op == CanvasOpIOS.PATH_CLIP_NONZERO || op.op == CanvasOpIOS.PATH_CLIP_EVEN_ODD;

    if (close) {
      c2d.closePath();
    }

    if (fill) {
      c2d.save();
      const alpha_ns = state.fromSaveStackBag('alpha_ns');
      if (alpha_ns !== null) {
        c2d.globalAlpha = alpha_ns;
      }
      c2d.fill(<any>fillRule);
      c2d.restore();
    }

    if (stroke) {
      c2d.save();
      const alpha_s = state.fromSaveStackBag('alpha_s');
      if (alpha_s !== null) {
        c2d.globalAlpha = alpha_s;
      }
      c2d.stroke();
      c2d.restore();
    }

    if (clip) {
      c2d.clip(<any>fillRule);
    }

    if (fill || stroke || end) {
      c2d.beginPath();
    }
  }
}

class VisualEffectBlurRenderer extends Renderer {
  // This category of effects is GPU accelerated on newer physical devices, and difficult to render in a browser.
  // For now, we'll just render a translucent color that is approximately correct.
  //
  // However, the details of the effect are recorded when possible, so that we can potentially recreated the fancy version in the future.
  //
  // Note: to have a 100% accurate playback, we'd need to figure out several other details including as the value of the "Reduce Transparancy",
  // "Increase Contrast", and "Differentiate Without Color" accessibility settings. (ch159953 & ch159955)

  // colors for all effects in light mode
  // note, this is a static property with a function to initialize it's value
  // values were computed by measuring pixel colors at each end of the gradient in the testing app
  // and assuming linear blending with #ffffff and #000000
  static colorsLight = (() => {
    const colors = {
      [IOSBlurEnum.SystemVibrantBackgroundRegular]: '#e9e9e9',

      [IOSBlurEnum.SystemUltraThinMaterialLight]: '#e1e1e15f',
      [IOSBlurEnum.SystemThinMaterialLight]: '#eeeeee92',
      [IOSBlurEnum.SystemMaterialLight]: '#f2f2f2c8',
      [IOSBlurEnum.SystemThickMaterialLight]: '#f3f3f3e9',

      [IOSBlurEnum.SystemChromeMaterialLight]: '#f4f4f4b3',
      [IOSBlurEnum.SystemChromeMaterialDark]: '#1a1a1abb',

      [IOSBlurEnum.SystemMaterialDark]: '#262626c9',
      [IOSBlurEnum.SystemThickMaterialDark]: '#202020f8',
      [IOSBlurEnum.SystemThinMaterialDark]: '#313131a1',
      [IOSBlurEnum.SystemUltraThinMaterialDark]: '#4545456b',

      // note: we also record a semi-transparent gray in a higher-up layer for these effects, as well as Regular and Promiment
      // which seems to produce the right colors, so do nothing here (until we can actually blur)
      [IOSBlurEnum.ExtraLight]: 'transparent',
      [IOSBlurEnum.Light]: 'transparent',
      [IOSBlurEnum.Dark]: 'transparent',
      [IOSBlurEnum.ExtraDark]: 'transparent',
    };

    Object.assign(colors, {
      // these effects use their light variants in light mode
      [IOSBlurEnum.SystemUltraThinMaterial]: colors[IOSBlurEnum.SystemUltraThinMaterialLight],
      [IOSBlurEnum.SystemThinMaterial]: colors[IOSBlurEnum.SystemThinMaterialLight],
      [IOSBlurEnum.SystemMaterial]: colors[IOSBlurEnum.SystemMaterialLight],
      [IOSBlurEnum.SystemThickMaterial]: colors[IOSBlurEnum.SystemThickMaterialLight],
      [IOSBlurEnum.SystemChromeMaterial]: colors[IOSBlurEnum.SystemChromeMaterialLight],

      [IOSBlurEnum.Regular]: colors[IOSBlurEnum.Light],
      [IOSBlurEnum.Prominent]: colors[IOSBlurEnum.ExtraLight],

      // Fallback for when we can't read the value
      // This is for things like UINavigationBar's visual effect - It uses a backgroundEffect that we can't see at all without using private APIs,
      // but in the debugger we can see that it's actually using UIBlurEffectStyleSystemChromeMaterial.
      [IOSBlurEnum.None]: colors[IOSBlurEnum.SystemChromeMaterialLight],
      // Fallback for when we can't parse the value
      [IOSBlurEnum.Unparseable]: colors[IOSBlurEnum.Light],
    });

    return colors;
  })();

  // colors for all effects in dark mode
  static colorsDark = (() => {
    // start with the colors from lightmode
    const colors = VisualEffectBlurRenderer.colorsLight;
    // then override the ones that change
    return {
      ...colors,
      [IOSBlurEnum.SystemVibrantBackgroundRegular]: '#404040',

      // these effects use their dark variants in dark mode
      [IOSBlurEnum.SystemUltraThinMaterial]: colors[IOSBlurEnum.SystemUltraThinMaterialDark],
      [IOSBlurEnum.SystemThinMaterial]: colors[IOSBlurEnum.SystemThinMaterialDark],
      [IOSBlurEnum.SystemMaterial]: colors[IOSBlurEnum.SystemMaterialDark],
      [IOSBlurEnum.SystemThickMaterial]: colors[IOSBlurEnum.SystemThickMaterialDark],
      [IOSBlurEnum.SystemChromeMaterial]: colors[IOSBlurEnum.SystemChromeMaterialDark],

      [IOSBlurEnum.Regular]: colors[IOSBlurEnum.Dark],
      [IOSBlurEnum.Prominent]: colors[IOSBlurEnum.ExtraDark],

      // Fallbacks
      [IOSBlurEnum.None]: colors[IOSBlurEnum.SystemChromeMaterialDark],
      [IOSBlurEnum.Unparseable]: colors[IOSBlurEnum.Dark],
    };
  })();

  draws(op: CanvasIOS) {
    return op.value != IOSBlurEnum.None;
  }

  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    // op.value holds the UIBlurEffectStyle enum value
    // A value of -1 indicates that we were unable to retrieve the effect, -2 means we failed to parse it (but we should have the text value in that case)
    // UINavigationBar's VisualEffectView is one expected example where -1 is expected: it uses a backgroundEffect (private API) rather than an effect (public)

    const blur = op.value;

    if (blur == IOSBlurEnum.Unparseable) {
      logWarnOnce(`Warning: unparseable UIBlurEffect: "${op.text}" on view ${state.view.uniqueId}`);
    }

    if (blur == IOSBlurEnum.None) {
      /* Some UIVisualEffectViews are actually nothing at all (i.e., they
       * are neither vibrancy nor blur).  We render those as transparent.
       */
      return;
    }
    c2d.save();
    const darkMode = state.view.flag(FlagsIOS.DARK_MODE);

    const colors = darkMode
      ? VisualEffectBlurRenderer.colorsDark
      : VisualEffectBlurRenderer.colorsLight;

    let color = colors[blur];

    if (!color) {
      logWarnOnce(
        `Missing background color definition for UIBlurEffect ${blur} (dark mode is ${darkMode}) on view ${state.view.uniqueId}`,
      );
      color = colors[IOSBlurEnum.Regular];
    }

    // The rect is needed for views that don't draw any other path (e.g. the VisualEffectView in a UINavigationBar).
    // It's not needed on views that draw their own clipping path (e.g. in a UIAlertControllerView) but appears to be safe.
    c2d.rect(0, 0, state.view.position.width(), state.view.position.height());

    c2d.fillStyle = color;
    c2d.fill();
    c2d.restore();
  }
}

function doesOverrideTint(vibrancy: IOSVibrancyEnum): boolean {
  if (vibrancy < 0) return false; // not real vibrancy values, either unparseable or "none"
  // of the real values, "Label" doesn't have this behavior
  return vibrancy !== IOSVibrancyEnum.Label;
}

class VisualEffectVibrancyRenderer extends Renderer {
  // This affects rendering of any subviews that rely on tint color
  // op.value holds the UIVibrancyEffectStyle enum value
  // op.flag1 holds the associated UIBlurEffectStyle enum value, defaulting to 0 if we were unable to retrieve/parse it (flag1 is a uint, so it cannot hold negative values)
  render(state: CanvasState, op: CanvasIOS, c2d: CanvasRenderingContext2D) {
    const vibrancy = op.value;
    if (vibrancy === IOSVibrancyEnum.Unparseable) {
      logWarnOnce(
        `Warning: unparseable UIVibrancyEffect: "${op.text}" on view ${state.view.uniqueId}`,
      );
    }
    // vibrancy effects seem to override the tint colors we find during capture
    // this is far from a complete solution - in general we'd need to check for things like
    // text rendering, where the apparent tint color is baked into the drawing ops already
    if (doesOverrideTint(vibrancy)) {
      state.saveStack[0].bag['vibrancy'] = '#808080';
    }
  }
}
